import type { GridApi } from '@mui/x-data-grid-pro';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { ItemsApi } from '@/common/api/items';
import type { Item, ItemV2 } from '@/common/api/types/items';
import { useGetCustomFieldsQuery } from '@/common/queries/useGetCustomFieldsQuery';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { itemV1ToV2 } from '@/features/items/utils/itemConvert';
import { VALID_V2_FIELD_IDS } from '../constants';
import { buildIdKeyMatcher } from '../hooks/buildIdKeyMatcher';
import { useCrossCachePagesSync } from '../hooks/useCrossCachePagesSync';
import type { DatasetPage } from '../hooks/useDatasetPages';
import { type ExtraItemsData, extrasQueryKey, useExtraItems } from '../hooks/useExtraItems';
import { type PageData, pageQueryKey, useTablePagedQuery } from '../hooks/useTablePagedQuery';

const ALL_V2_FIELD_IDS: readonly string[] = Array.from(VALID_V2_FIELD_IDS);
const EXTRAS_FIELD_IDS: readonly string[] = ['id', 'itemTypeId', 'name'];

const EMPTY_ITEMS_BY_ID: ReadonlyMap<number, Partial<ItemV2>> = new Map();
const EMPTY_RESTRICTED_IDS: ReadonlySet<number> = new Set();
const matchItemKey = buildIdKeyMatcher(commonQueryKeys.item);
const itemFromSource = (_id: number, sourceData: unknown): Partial<ItemV2> | undefined => {
  if (!sourceData) return undefined;
  return itemV1ToV2(sourceData as Item);
};

// Paged cache wins because it holds the full ItemV2 shape; extras only carries
// (id/itemTypeId/name) and would clobber a fully-loaded row if it overwrote.
const fillGapsWithExtras = (
  paged: ReadonlyMap<number, Partial<ItemV2>>,
  extras: ReadonlyMap<number, Partial<ItemV2>>,
): ReadonlyMap<number, Partial<ItemV2>> => {
  const result = new Map(paged);
  for (const [id, value] of extras) {
    if (!result.has(id)) result.set(id, value);
  }
  return result;
};

export type SyncSource<K extends number | string, V> = {
  matchKey: (queryKey: readonly unknown[]) => K | null;
  transform: (id: K, sourceData: unknown) => V | undefined;
};

export type FetchItemsByIds = (
  ids: readonly number[],
  signal: AbortSignal,
) => Promise<ReadonlyMap<number, Partial<ItemV2>>>;

type VisibleItemsContextType = {
  itemsById: ReadonlyMap<number, Partial<ItemV2>>;
  restrictedIds: ReadonlySet<number>;
  isPending: boolean;
  instanceKey: string;
  registerExtraId: (id: number) => void;
  unregisterExtraId: (id: number) => void;
  patchItem: (id: number, patch: Partial<ItemV2>) => Partial<ItemV2> | undefined;
  reconcileItemAfterMutation: (id: number, item: Partial<ItemV2>) => void;
  setItem: (item: Partial<ItemV2>) => void;
};

const VisibleItemsContext = React.createContext<VisibleItemsContextType | undefined>(undefined);

export const useVisibleItems = (): VisibleItemsContextType => {
  const context = React.useContext(VisibleItemsContext);
  if (!context) {
    return {
      itemsById: EMPTY_ITEMS_BY_ID,
      restrictedIds: EMPTY_RESTRICTED_IDS,
      isPending: false,
      instanceKey: '',
      registerExtraId: () => {},
      unregisterExtraId: () => {},
      patchItem: () => undefined,
      reconcileItemAfterMutation: () => {},
      setItem: () => {},
    };
  }
  return context;
};

export const useRegisterExtraItemId = (id: number | undefined): void => {
  const { registerExtraId, unregisterExtraId } = useVisibleItems();
  React.useEffect(() => {
    if (id === undefined) return;
    registerExtraId(id);
    return () => unregisterExtraId(id);
  }, [id, registerExtraId, unregisterExtraId]);
};

export const useRegisterExtraItemIds = (ids: readonly number[] | undefined): void => {
  const { registerExtraId, unregisterExtraId } = useVisibleItems();
  const idsKey = ids?.join('|') ?? '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: idsKey is a stable string derived from ids
  React.useEffect(() => {
    if (!ids?.length) return;
    for (const id of ids) registerExtraId(id);
    return () => {
      for (const id of ids) unregisterExtraId(id);
    };
  }, [idsKey, registerExtraId, unregisterExtraId]);
};

export const useVisibleItem = (id: number | undefined): Partial<ItemV2> | undefined => {
  const { itemsById } = useVisibleItems();
  if (id === undefined) return undefined;
  return itemsById.get(id);
};

const buildBulkFetcher =
  (fieldIds: readonly string[], customFieldIds: readonly number[] | undefined) =>
  async (
    ids: readonly number[],
    signal: AbortSignal,
  ): Promise<ReadonlyMap<number, Partial<ItemV2>>> => {
    const idsArray = [...ids];
    // Delegate chunking to the shared helper so large viewports inherit its
    // concurrency cap instead of bursting every chunk at once.
    const response = await ItemsApi.getItemsBulkFetchV2Chunked({
      ids: idsArray,
      fieldIds: fieldIds as string[],
      customFieldIds: customFieldIds ? (customFieldIds as number[]) : undefined,
      signal,
    });
    return new Map(Object.entries(response.itemsById).map(([key, item]) => [Number(key), item]));
  };

const createFetchItemsV2 = (customFieldIds: readonly number[]) =>
  buildBulkFetcher(ALL_V2_FIELD_IDS, customFieldIds);

// Extras fetcher: slim projection covering only what cell consumers
// (ItemRefCell / MultiItemRefCell) render — id, item type, name. Skipping
// customFieldIds and the heavy V2 fields drops the per-item payload by an
// order of magnitude, which is what makes the inevitable double-fetch (extras
// now, paged later when the user scrolls) cheap enough to tolerate.
const createFetchExtrasItemsV2 = () => buildBulkFetcher(EXTRAS_FIELD_IDS, undefined);

/**
 * Bump all page cache entries for this provider/instance so subscribers
 * (rows, cells) re-render after a side-effecting mutation that doesn't
 * actually change the per-id values. Used by mutation hooks that re-fetch
 * the tree-set and need the grid to re-render against the new structure.
 */
export const bumpVisibleItemsCache = (queryClient: QueryClient, instanceKey: string): void => {
  const entries = queryClient.getQueriesData<PageData<Partial<ItemV2>>>({
    queryKey: [commonQueryKeys.itemsTable.visibleItems, instanceKey, 'page'],
  });
  for (const [key, data] of entries) {
    if (!data) continue;
    queryClient.setQueryData<PageData<Partial<ItemV2>>>(key, {
      valuesById: new Map(data.valuesById),
      missingIds: data.missingIds,
    });
  }
};

/**
 * Drop every cached page entry for this provider/instance whose dataset
 * signature differs from `activeSignature`. Each filter/sort state owns its
 * own page cache (keyed by signature) with staleTime: Infinity; after a
 * mutation those sibling caches would otherwise keep serving pre-mutation
 * values that never refetch. Removing them makes the next visit to that
 * filter state re-fetch fresh server data — the same path a filter change
 * with no prior edit already takes.
 */
const removeSiblingSignaturePages = (
  queryClient: QueryClient,
  instanceKey: string,
  activeSignature: string,
): void => {
  queryClient.removeQueries({
    predicate: (query) => {
      const queryKey = query.queryKey;
      return (
        Array.isArray(queryKey) &&
        queryKey[0] === commonQueryKeys.itemsTable.visibleItems &&
        queryKey[1] === instanceKey &&
        queryKey[2] === 'page' &&
        queryKey[3] !== activeSignature
      );
    },
  });
};

/**
 * Drop every cached page entry for this provider/instance, across all dataset
 * signatures. Callers that live outside the VisibleItemsProvider (e.g. the bulk
 * edit toolbar) can't reach `reconcileItemAfterMutation`, so after a mutation
 * they evict the whole instance: the currently mounted signature refetches
 * immediately, and every other filter/sort signature — which is pinned with
 * staleTime: Infinity — refetches fresh the next time it is shown instead of
 * serving pre-mutation values until a hard refresh.
 */
export const removeVisibleItemsPagesForInstance = (
  queryClient: QueryClient,
  instanceKey: string,
): void => {
  queryClient.removeQueries({
    predicate: (query) => {
      const queryKey = query.queryKey;
      return (
        Array.isArray(queryKey) &&
        queryKey[0] === commonQueryKeys.itemsTable.visibleItems &&
        queryKey[1] === instanceKey &&
        queryKey[2] === 'page'
      );
    },
  });
};

export const VisibleItemsProvider = ({
  pages,
  visiblePageIndices,
  pageIndexById,
  datasetSignature,
  apiRef,
  instanceKey,
  enabled = true,
  staticItems,
  fetchItemsByIds,
  itemsSyncSource,
  children,
}: {
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  apiRef: React.RefObject<GridApi>;
  instanceKey: string;
  enabled?: boolean;
  staticItems?: readonly Item[];
  fetchItemsByIds?: FetchItemsByIds;
  itemsSyncSource?: SyncSource<number, Partial<ItemV2>>;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const { data: customFields, isLoading: customFieldsLoading } = useGetCustomFieldsQuery();
  const customFieldIds = React.useMemo(() => customFields?.map((f) => f.id) ?? [], [customFields]);
  const fetchByIds = React.useMemo(
    () => fetchItemsByIds ?? createFetchItemsV2(customFieldIds),
    [fetchItemsByIds, customFieldIds],
  );
  const fetchExtrasByIds = React.useMemo(
    () => fetchItemsByIds ?? createFetchExtrasItemsV2(),
    [fetchItemsByIds],
  );

  const pagedQueryEnabled = enabled && !customFieldsLoading && !staticItems;
  const pagedItemsByIdRef = React.useRef<ReadonlyMap<number, unknown>>(new Map());

  const {
    loadedById: pagedItemsById,
    missingIds: pagedMissingIds,
    isPending: pagedPending,
  } = useTablePagedQuery<Partial<ItemV2>>({
    providerName: commonQueryKeys.itemsTable.visibleItems,
    instanceKey,
    pages,
    visiblePageIndices,
    pageIndexById,
    datasetSignature,
    fetchByIds,
    enabled: pagedQueryEnabled,
    apiRef,
  });

  pagedItemsByIdRef.current = pagedItemsById;

  // Extras: ids referenced by cells (e.g. ITEM/MULTI_ITEM custom-field values)
  // that the paged query hasn't loaded. Uses a slim fetcher (id/itemTypeId/name)
  // and dedup against the paged cache only — see docstring on `useExtraItems`.
  const {
    data: extrasData,
    registerExtraId,
    unregisterExtraId,
  } = useExtraItems({
    instanceKey,
    fetchByIds: fetchExtrasByIds,
    loadedItemsRef: pagedItemsByIdRef,
    enabled: pagedQueryEnabled,
  });

  // Reflect cross-app writes/refetches of commonQueryKeys.item(id) into the
  // paged cache.
  useCrossCachePagesSync<Partial<ItemV2>>({
    providerName: commonQueryKeys.itemsTable.visibleItems,
    instanceKey,
    pageIndexById,
    datasetSignature,
    matchKey: itemsSyncSource?.matchKey ?? matchItemKey,
    transform: itemsSyncSource?.transform ?? itemFromSource,
  });

  const patchItem = React.useCallback(
    (id: number, patch: Partial<ItemV2>): Partial<ItemV2> | undefined => {
      const pageIndex = pageIndexById.get(id);
      if (pageIndex !== undefined) {
        const key = pageQueryKey(
          commonQueryKeys.itemsTable.visibleItems,
          instanceKey,
          datasetSignature,
          pageIndex,
        );
        const current = queryClient.getQueryData<PageData<Partial<ItemV2>>>(key);
        const previousItem = current?.valuesById.get(id);
        if (!current || !previousItem) return undefined;
        const next = new Map(current.valuesById);
        next.set(id, { ...previousItem, ...patch });
        queryClient.setQueryData<PageData<Partial<ItemV2>>>(key, {
          valuesById: next,
          missingIds: current.missingIds,
        });
        return previousItem;
      }
      const extrasKey = extrasQueryKey(instanceKey);
      const current = queryClient.getQueryData<ExtraItemsData>(extrasKey);
      const previousItem = current?.valuesById.get(id);
      if (!current || !previousItem) return undefined;
      const next = new Map(current.valuesById);
      next.set(id, { ...previousItem, ...patch });
      queryClient.setQueryData<ExtraItemsData>(extrasKey, {
        valuesById: next,
        missingIds: current.missingIds,
      });
      return previousItem;
    },
    [queryClient, instanceKey, pageIndexById, datasetSignature],
  );

  // Reconcile a mutated item after the server confirms it. Patch the active
  // signature in place so the visible view repaints instantly, then drop the
  // sibling-signature page caches so any other filter/sort state re-fetches
  // fresh server data the next time it is shown. Writing into those sibling
  // caches directly is unsafe — they have staleTime: Infinity and never
  // refetch, so a partial or stale write sticks until a hard refresh.
  const reconcileItemAfterMutation = React.useCallback(
    (id: number, item: Partial<ItemV2>): void => {
      patchItem(id, item);
      removeSiblingSignaturePages(queryClient, instanceKey, datasetSignature);
    },
    [patchItem, queryClient, instanceKey, datasetSignature],
  );

  const setItem = React.useCallback(
    (item: Partial<ItemV2>) => {
      const id = item.id;
      if (id === undefined) return;
      const pageIndex = pageIndexById.get(id);
      if (pageIndex !== undefined) {
        const key = pageQueryKey(
          commonQueryKeys.itemsTable.visibleItems,
          instanceKey,
          datasetSignature,
          pageIndex,
        );
        const current = queryClient.getQueryData<PageData<Partial<ItemV2>>>(key);
        if (!current) return;
        const next = new Map(current.valuesById);
        next.set(id, item);
        queryClient.setQueryData<PageData<Partial<ItemV2>>>(key, {
          valuesById: next,
          missingIds: current.missingIds,
        });
        return;
      }
      const extrasKey = extrasQueryKey(instanceKey);
      const current = queryClient.getQueryData<ExtraItemsData>(extrasKey);
      if (!current) return;
      const next = new Map(current.valuesById);
      next.set(id, item);
      queryClient.setQueryData<ExtraItemsData>(extrasKey, {
        valuesById: next,
        missingIds: current.missingIds,
      });
    },
    [queryClient, instanceKey, pageIndexById, datasetSignature],
  );

  const staticItemsById = React.useMemo<ReadonlyMap<number, Partial<ItemV2>> | undefined>(() => {
    if (!staticItems) return undefined;
    return new Map(staticItems.map((item) => [item.id, itemV1ToV2(item)]));
  }, [staticItems]);

  const itemsById = React.useMemo<ReadonlyMap<number, Partial<ItemV2>>>(() => {
    if (staticItemsById) return staticItemsById;
    if (!extrasData || extrasData.valuesById.size === 0) {
      return pagedItemsById.size > 0 ? pagedItemsById : EMPTY_ITEMS_BY_ID;
    }
    return fillGapsWithExtras(pagedItemsById, extrasData.valuesById);
  }, [staticItemsById, pagedItemsById, extrasData]);

  const restrictedIds = React.useMemo<ReadonlySet<number>>(() => {
    if (staticItemsById) return EMPTY_RESTRICTED_IDS;
    if (!extrasData || extrasData.missingIds.size === 0) {
      return pagedMissingIds.size > 0 ? pagedMissingIds : EMPTY_RESTRICTED_IDS;
    }
    const merged = new Set(pagedMissingIds);
    for (const id of extrasData.missingIds) merged.add(id);
    return merged;
  }, [staticItemsById, pagedMissingIds, extrasData]);

  const value = React.useMemo<VisibleItemsContextType>(
    () => ({
      itemsById,
      restrictedIds,
      isPending: !staticItemsById && pagedPending,
      instanceKey,
      registerExtraId,
      unregisterExtraId,
      patchItem,
      reconcileItemAfterMutation,
      setItem,
    }),
    [
      itemsById,
      restrictedIds,
      staticItemsById,
      pagedPending,
      instanceKey,
      registerExtraId,
      unregisterExtraId,
      patchItem,
      reconcileItemAfterMutation,
      setItem,
    ],
  );

  return <VisibleItemsContext.Provider value={value}>{children}</VisibleItemsContext.Provider>;
};
